import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Shared";
import PatientDialog from "@/components/PatientDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UserPlus, MagnifyingGlass, FirstAidKit, CaretRight } from "@phosphor-icons/react";

export default function Patients() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const canWrite = user?.permissions?.includes("patient:write");

  const load = useCallback(async (query = "") => {
    setLoading(true);
    try { const { data } = await api.get("/clinic/patients", { params: { q: query } }); setPatients(data); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); api.get("/clinic/meta").then(({ data }) => setDeviceTypes(data.device_types)); }, [load]);
  useEffect(() => { const t = setTimeout(() => load(q), 300); return () => clearTimeout(t); }, [q, load]);

  return (
    <div data-testid="patients-page">
      <PageHeader title="Patients" subtitle="Register and manage prosthetics & orthotics patients">
        {canWrite && (
          <Button data-testid="add-patient-button" onClick={() => setOpen(true)} className="bg-primary hover:bg-primary/90 gap-2">
            <UserPlus size={17} weight="bold" /> New patient
          </Button>
        )}
      </PageHeader>

      <div className="p-6 md:p-8 space-y-5">
        <div className="relative max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="patient-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, phone, email or patient no…" className="pl-9" />
        </div>

        {loading ? (
          <div className="h-40 flex items-center justify-center text-muted-foreground text-sm">Loading patients…</div>
        ) : patients.length === 0 ? (
          <div className="h-56 flex flex-col items-center justify-center text-muted-foreground gap-3 border border-dashed border-border rounded-md">
            <FirstAidKit size={28} />
            <p className="text-sm">{q ? "No patients match your search." : "No patients registered yet."}</p>
            {canWrite && !q && <Button size="sm" variant="outline" onClick={() => setOpen(true)}>Register your first patient</Button>}
          </div>
        ) : (
          <div className="bg-card border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-left">
                <tr>
                  {["Patient no", "Name", "Gender / Age", "Contact", "Device(s)", ""].map((h, i) => (
                    <th key={i} className="px-4 py-3 font-medium text-xs uppercase tracking-wider text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {patients.map((p) => (
                  <tr key={p.id} data-testid={`patient-row-${p.id}`} onClick={() => navigate(`/clinic/patients/${p.id}`)}
                    className="hover:bg-accent/40 transition-colors duration-150 cursor-pointer">
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{p.patient_no}</td>
                    <td className="px-4 py-3 font-medium">{p.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{[p.gender, p.age].filter(Boolean).join(" · ") || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{p.phone || "—"}</td>
                    <td className="px-4 py-3 text-muted-foreground">{(p.device_types || []).join(", ") || "—"}</td>
                    <td className="px-4 py-3 text-right"><CaretRight size={15} className="text-muted-foreground" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PatientDialog open={open} onOpenChange={setOpen} deviceTypes={deviceTypes} onSaved={() => load(q)} />
    </div>
  );
}
